/**
 * Navigation runtime core.
 *
 * Intercepts eligible internal link clicks and performs a soft navigation:
 * fetch the target page, reconcile the top chrome island (header, banner)
 * AND #MainContent against the incoming document — the incoming markup is
 * server-rendered per request.page_type, so page-dependent chrome state
 * (transparent mode, nav active state, cart badge) is correct by
 * construction — append any target stylesheet link the live document is
 * missing, run the page scripts, update the URL/title/meta, and wrap the
 * swap in a same document view transition when supported. Anything that
 * cannot be handled falls back to a full navigation — never a blank
 * screen.
 *
 * Design doc: docs/navigation-runtime.md (gitignored, local reference).
 *
 * @module navigation
 */

import {
  executePageScripts,
  reconcileMissingStyles,
  seedExecutedScripts,
} from '@theme/nav-resources';
import { morphSafe } from '@theme/morph';

/** Route prefixes that always use full navigation (stateful/secure). */
const EXCLUDED_PREFIXES = [
  '/account', // locale-ok: root-stripped comparison value, not a URL
  '/cart', // locale-ok: root-stripped comparison value, not a URL
  '/challenge', // locale-ok: root-stripped comparison value, not a URL
  '/checkout', // locale-ok: root-stripped comparison value, not a URL
  '/gift_cards', // locale-ok: root-stripped comparison value, not a URL
  '/orders', // locale-ok: root-stripped comparison value, not a URL
  '/password', // locale-ok: root-stripped comparison value, not a URL
];

/** Max remembered page payloads for back/forward within a session. */
const CACHE_LIMIT = 20;

/** Delay before the top progress bar becomes visible on a soft navigation.
 *  A navigation that finishes (or is aborted) within this window never
 *  shows the bar at all: on a warm/fast network most swaps land well under
 *  ~250ms, where an instantly-appearing bar is just a ~1.4s flash of noise
 *  (audit P2-1). 250ms sits inside the user signed-off 200-300ms band —
 *  above virtually every fast navigation, yet short enough that the bar
 *  still appears long before a slow wait feels unresponsive. */
const PROGRESS_BAR_SHOW_DELAY_MS = 250;

/** State class toggled on <body> while a soft navigation waits. CSS in
 *  common.css ("soft navigation pending state" section) turns it into the
 *  slow-network skeleton look: content sections fade to a ghost and sweep
 *  a highlight band, both gated behind a 400ms CSS delay, so a navigation
 *  that lands inside the threshold never renders a pending frame. The
 *  class is attached on start (never for history traversals — their swap
 *  is effectively synchronous) and removed on every exit path; both
 *  directions are idempotent. */
const NAV_PENDING_CLASS = 'nav-pending';

const cachedPages = new Map();
const display = {
  start: 'site:navigation:start',
  commit: 'site:navigation:commit',
  end: 'site:navigation:end',
  error: 'site:navigation:error',
  fallback: 'site:navigation:fallback',
};

let rootUrl = window.Shopify?.routes?.root || '/'; // locale-ok: last resort; window.Shopify.routes.root is guaranteed on a storefront
let currentTemplate = '';
let inflight = null;
let ready = false;
let traversing = false;
let pendingTraversal = null;

/** document.title as it stood before the current uncommitted navigation
 *  chain first changed it (captured by syncHead, released once content
 *  lands). An aborted navigation restores it so the tab and the page:view
 *  attribution keep belonging to the page the visitor is actually on. */
let savedTitle = null;

/** Monotonic navigation epoch. Bumped at every perform() start; an abort
 *  checkpoint may only clean up while its own epoch is still current. A
 *  point-in-time inflight check cannot do this: inflight is cleared when
 *  ANY navigation completes, so a superseded navigation's deferred abort
 *  (gate poll, view-transition finish) would mistake a successor that
 *  already landed for "no successor" and rewrite its history entry. */
let navEpoch = 0;

/**
 * Runs a callback once the document is fully parsed and every page script
 * (module and classic) has executed — the safe moment to seed the executed
 * scripts set and start intercepting clicks.
 *
 * @param {() => void} callback
 */
function whenReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

/**
 * Reads the layout-rendered runtime config (JSON data, no inline JS).
 *
 * @returns {{ templateName: string, rootUrl: string }}
 */
function readConfig() {
  try {
    const element = document.getElementById('theme-config');
    if (!element) {
      return { templateName: '', rootUrl: window.Shopify?.routes?.root || '/' }; // locale-ok: last resort; window.Shopify.routes.root is guaranteed on a storefront
    }
    return JSON.parse(element.textContent);
  } catch {
    return { templateName: '', rootUrl: window.Shopify?.routes?.root || '/' }; // locale-ok: last resort; window.Shopify.routes.root is guaranteed on a storefront
  }
}

/**
 * Dispatches a bubbling CustomEvent on document.
 *
 * @param {string} name
 * @param {Object} detail
 */
function dispatch(name, detail) {
  document.dispatchEvent(
    new CustomEvent(name, { bubbles: true, detail: { ...detail } })
  );
}

/**
 * Absolute path of a URL relative to the store root (multi-market aware).
 *
 * @param {URL} url
 * @returns {string}
 */
function pathRelativeToRoot(url) {
  const path = url.pathname.startsWith(rootUrl)
    ? url.pathname.slice(rootUrl.length)
    : url.pathname;
  return path.startsWith('/') ? path : `/${path}`;
}

/** Cached set of every market/locale prefix this store publishes. */
let knownMarketRoots = null;

/**
 * Every market/locale prefix the storefront publishes. Shopify emits one
 * rel="alternate" hreflang link per localized URL, so those hrefs are the
 * authoritative list: a link already pointing at one of them is a deliberate
 * cross-market link and must be left exactly as the author wrote it.
 *
 * @returns {string[]}
 */
function marketRoots() {
  if (knownMarketRoots) {
    return knownMarketRoots;
  }

  const roots = new Set();
  const alternates = document.querySelectorAll('link[rel="alternate"][hreflang]');
  for (const link of alternates) {
    try {
      const path = new URL(link.href, location.href).pathname.replace(/\/+$/, '');
      roots.add(path === '' ? '/' : path);
    } catch {
      // A malformed alternate link must never break navigation.
    }
  }

  knownMarketRoots = [...roots];
  return knownMarketRoots;
}

/**
 * Keeps a soft navigation inside the visitor's market.
 *
 * Shopify's market auto-redirect is a server-side 302 that only applies to
 * top-level document navigations — a fetch() bypasses it entirely. Theme
 * editor link settings are stored as plain paths carrying no locale prefix,
 * so without this a China-market visitor clicking such a link silently gets
 * the primary market's page while the URL keeps the unprefixed path, leaving
 * the address bar, the page language and the market markers disagreeing.
 *
 * @param {URL} url - Resolved internal link target
 * @returns {URL} - The same URL, or a market-prefixed copy
 */
function withMarketPrefix(url) {
  if (!rootUrl || rootUrl === '/') { // locale-ok: compares the resolved routes.root, never navigates to a literal
    return url; // primary market: every path is already in-market
  }

  const path = url.pathname;
  if (path === rootUrl || path.startsWith(`${rootUrl}/`)) {
    return url;
  }

  for (const root of marketRoots()) {
    if (root === '/' || root === rootUrl) { // locale-ok: filters the primary-market root out of the compare set
      continue;
    }
    if (path === root || path.startsWith(`${root}/`)) {
      return url; // deliberate link into another market — respect it
    }
  }

  const prefixed = new URL(url.href);
  prefixed.pathname = `${rootUrl}${path}`;
  return prefixed;
}

/**
 * Whether a link should be handled by the soft navigation runtime.
 *
 * @param {string} href - The link's href (may be relative)
 * @returns {boolean}
 */
export function isEligible(href) {
  let url;
  try {
    url = new URL(href, location.href);
  } catch {
    return false;
  }

  if (url.origin !== location.origin) {
    return false;
  }
  if (url.href === location.href) {
    return false;
  }

  const current = new URL(location.href);
  const sameDocument =
    url.pathname === current.pathname && url.search === current.search;
  if (sameDocument) {
    return false;
  }

  const relativePath = pathRelativeToRoot(url);
  for (const prefix of EXCLUDED_PREFIXES) {
    if (relativePath === prefix || relativePath.startsWith(`${prefix}/`)) {
      return false;
    }
  }

  return true;
}

/**
 * Fetches a page as HTML with the host's same-origin credentials.
 *
 * @param {URL} url
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
async function fetchPage(url, signal) {
  const response = await fetch(url.href, {
    headers: { Accept: 'text/html' },
    credentials: 'same-origin',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Soft navigation fetch failed: ${response.status}`);
  }
  return response.text();
}

/**
 * LRU caches a raw page payload for back/forward restores.
 *
 * @param {string} url
 * @param {string} html
 */
function cachePage(url, html) {
  cachedPages.delete(url);
  cachedPages.set(url, html);
  if (cachedPages.size > CACHE_LIMIT) {
    cachedPages.delete(cachedPages.keys().next().value);
  }
}

/**
 * Copies title, lang, market markers and key head tags from the incoming
 * document.
 *
 * The outgoing title is captured first (once per uncommitted chain) so an
 * aborted navigation can restore the page the visitor is still on.
 *
 * @param {Document} incoming
 */
function syncHead(incoming) {
  if (savedTitle === null) {
    savedTitle = document.title;
  }

  const title = incoming.head.querySelector('title');
  if (title && title.textContent) {
    document.title = title.textContent;
  }

  const lang = incoming.documentElement.getAttribute('lang');
  if (lang) {
    document.documentElement.setAttribute('lang', lang);
  }

  // Market markers sit on <html> next to lang. The chrome around #MainContent
  // is never replaced, so they have to be synced explicitly — otherwise a
  // market-crossing navigation leaves contradictory state (assets/product-form.js
  // reads data-market-cn to choose the China purchase path).
  document.documentElement.toggleAttribute(
    'data-market-cn',
    incoming.documentElement.hasAttribute('data-market-cn')
  );

  const pairs = [
    ['meta[name="description"]', 'meta[name="description"]'],
    ['meta[name="theme-color"]', 'meta[name="theme-color"]'],
    ['meta[property^="og:"]', 'meta[property^="og:"]'],
    ['link[rel="canonical"]', 'link[rel="canonical"]'],
  ];
  for (const [fromSelector, toSelector] of pairs) {
    for (const element of document.querySelectorAll(toSelector)) {
      element.remove();
    }
    for (const element of incoming.head.querySelectorAll(fromSelector)) {
      document.head.appendChild(element.cloneNode(true));
    }
  }
}

/**
 * Restores the title of the page the visitor is actually on after an
 * aborted soft navigation; a no-op while no navigation has touched the
 * title. savedTitle is released once a swap lands — the target page's
 * title then becomes the document's truth.
 */
function restoreTitle() {
  if (savedTitle !== null) {
    document.title = savedTitle;
    savedTitle = null;
  }
}

/**
 * Replaces the children of the live #MainContent with the incoming
 * page's main content, then appends any stylesheet the incoming document
 * references that the live head does not carry yet.
 *
 * @param {Document} incoming
 */
function applySwap(incoming) {
  const currentMain = document.getElementById('MainContent');
  const incomingMain = incoming.getElementById('MainContent');
  if (!currentMain || !incomingMain) {
    throw new Error('Soft navigation failed: #MainContent missing');
  }
  currentMain.replaceChildren(...incomingMain.childNodes);
  reconcileMissingStyles(incoming);
}

/**
 * Reconciles the live top chrome island (market banner + header + mega
 * panel + drawers) against the incoming document's island.
 *
 * The chrome is a page-level region like #MainContent, and its markup is
 * server-rendered per request.page_type — transparent mode, nav active
 * state, scroll-mode attribute, cart badge, all of it. Morphing it inside
 * the swap callback makes the incoming document the single source of truth
 * for page-dependent chrome state: nothing is guessed in JS, so nothing
 * can go stale or desync across soft navigations. The morph is keyed on
 * section ids, so custom-element instances (and the header's
 * one-per-document inline loader module) are preserved; only attributes
 * and content that actually changed are touched.
 *
 * @param {Document} incoming
 */
function applyChromeSwap(incoming) {
  const currentChrome = document.querySelector('.chrome-island');
  const incomingChrome = incoming.querySelector('.chrome-island');
  if (!currentChrome || !incomingChrome) {
    return;
  }
  morphSafe(currentChrome, incomingChrome);
}

/**
 * Dispatches shopify:page:view (standard events, platform CDN) for the
 * current page. Progressive enhancement: if the standard-events runtime is
 * unavailable the navigation itself is unaffected.
 *
 * @param {string} template - Liquid template name of the visible page
 */
async function dispatchPageView(template) {
  if (!template) {
    return;
  }
  try {
    const { PageViewEvent } = await import('@shopify/standard-events');
    document.dispatchEvent(
      new PageViewEvent({
        page: {
          template,
          title: document.title,
          url: window.location.href,
        },
      })
    );
  } catch {
    // Standard events are an enhancement; never block navigation on them.
  }
}

/**
 * Reads the incoming document's own template name (its layout renders the
 * same theme-config JSON) so page:view carries the true target template.
 *
 * @param {Document} incoming
 * @returns {string}
 */
function readIncomingTemplateName(incoming) {
  try {
    const element = incoming.getElementById('theme-config');
    if (!element) {
      return currentTemplate;
    }
    const config = JSON.parse(element.textContent);
    return config.templateName || currentTemplate;
  } catch {
    return currentTemplate;
  }
}

/**
 * Scrolls to the element named by a URL fragment — the soft navigation
 * equivalent of the browser's native cross-page fragment jump. Anchor
 * targets carry scroll-margin-top (site convention, e.g. the support page
 * sections in static-pages.css), so scrollIntoView stops with the section
 * start clear of the sticky header — the same treatment as the in-page
 * anchor scrolling in mega-menu.js and support-detail.js.
 *
 * @param {string} hash - The fragment of the target URL, including '#'
 * @returns {boolean} Whether a matching element was found and scrolled to
 */
function scrollToAnchor(hash) {
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    // Malformed fragment — fall back to the raw id.
  }
  const element = document.getElementById(id);
  if (!element) {
    return false;
  }
  element.scrollIntoView({ block: 'start' });
  return true;
}

/**
 * Moves focus to the main content so assistive tech lands on the new page.
 *
 * Runs after every soft navigation's swap has fully landed — pushed clicks
 * and history traversals alike, on both the animated (view-transition) and
 * reduced-motion paths. #MainContent carries tabindex="-1" in the layout, so
 * this is a pure programmatic focus; preventScroll keeps whatever scroll
 * position the swap established (top for a click, restored for back/forward)
 * intact. See the focus contract in Catsights-软导航军规.md.
 */
function focusMain() {
  const main = document.getElementById('MainContent');
  if (!main) {
    return;
  }
  main.setAttribute('tabindex', '-1');
  main.focus({ preventScroll: true });
}

/** Reduced-motion users get the no-view-transition path. The cross-document
 *  runtime guards this same preference (see assets/view-transitions.js); the
 *  same-document soft navigation must honor it too, or the UA's default
 *  cross-fade still plays after the theme's own animations are disabled. */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Runs one soft navigation to `target`.
 *
 * @param {URL} target
 * @param {Object} [options]
 * @param {AbortController} [options.controller]
 * @param {boolean} [options.traverse] - History traversal (popstate): do
 *   not touch the URL, restore the given scroll position
 * @param {number} [options.restoreScroll]
 */
async function perform(target, options = {}) {
  const { controller, traverse = false, restoreScroll = 0 } = options;
  const fromHref = location.href;
  const myEpoch = ++navEpoch;
  let swapped = false;

  /* Every abort detection point throws through here so an interrupted
     navigation leaves nothing behind. While the swap has not landed, the
     document still belongs to the page the visitor is on: its title is
     restored (syncHead may already have moved it to the abandoned target),
     and — while this navigation is still the newest one that ever started
     (its epoch is current), i.e. Escape rather than any newer navigation —
     a history entry it had already pushed is corrected back to the
     originating URL. A landed swap owns the document (the target page IS
     the visible page then; only its end event is withheld) and ANY newer
     navigation — running, aborted, or already landed — owns the
     URL/title timeline from the moment it started, so in those cases
     everything is left alone. */
  const abort = () => {
    if (!swapped && navEpoch === myEpoch) {
      restoreTitle();
      if (location.href !== fromHref) {
        history.replaceState(
          { navigation: true, scrollY: window.scrollY },
          '',
          fromHref
        );
      }
    }
    throw new DOMException('Aborted', 'AbortError');
  };

  dispatch(display.start, {
    to: target.href,
    fromPath: location.pathname,
    traverse,
  });

  const html =
    cachedPages.get(target.href) ??
    (await fetchPage(target, controller.signal));
  if (controller.signal.aborted) {
    abort();
  }
  cachePage(target.href, html);

  const incoming = new DOMParser().parseFromString(html, 'text/html');
  syncHead(incoming);
  currentTemplate = readIncomingTemplateName(incoming);

  if (!traverse) {
    // Snapshot the outgoing page's scroll, then land on top of the new page.
    history.replaceState(
      { navigation: true, scrollY: window.scrollY },
      '',
      location.href
    );
    history.pushState({ navigation: true, scrollY: 0 }, '', target.href);
  }

  const swap = () => {
    /* The view transition runs this update callback on a LATER frame: an
       abort (Escape or a newer navigation) can land in the window between
       the post-gate check above and this callback executing. A zombie swap
       of the abandoned page must never overwrite the DOM — the visible
       content always belongs to the LAST navigation. */
    if (controller.signal.aborted) {
      return;
    }
    swapped = true;
    // syncHead has already made the incoming title the document's title, so
    // the old page's title is stale the moment the swap begins — release it
    // even if applySwap itself throws, or the next chain's abort would
    // restore a title that no longer belongs to any visible page.
    try {
      applySwap(incoming);
      applyChromeSwap(incoming);
    } finally {
      savedTitle = null;
    }
    if (!traverse) {
      // A cross-page hash link (/page#anchor) must land on its anchor the
      // way a full page load does; without a matching element (or without
      // a hash at all) the navigation lands on top as before.
      if (!target.hash || !scrollToAnchor(target.hash)) {
        window.scrollTo(0, 0);
      }
    }
    // Page-dependent resident state (chrome offset, header mode, takeover
    // detection) must settle BEFORE the view transition captures the new
    // state — it snapshots right after this callback returns. The commit
    // event fires synchronously so chrome/header re-derive from the freshly
    // morphed DOM inside the same task. The transition lock strips the
    // shell's transform transition for the duration of the commit: a live
    // offset reset would otherwise be captured at its pre-transition value
    // (the browser freezes the new state before any transition progresses —
    // verified empirically), which is exactly the temporary disappearing
    // header this replaces.
    document.documentElement.setAttribute('data-chrome-committing', '');
    dispatch(display.commit, { to: target.href, traverse });
    // Content landed: drop this navigation's pending bar reveal before the
    // display threshold fires (no-op when the bar is already visible — its
    // END-driven hide is unchanged). The aborted early-return above leaves
    // a newer navigation's pending reveal alone.
    progressBar.cancelPendingShow();
    // Content landed: drop the pending look inside the same update
    // callback. A view transition snapshots the old page (skeleton look
    // and all) before this runs and the new page after it, so the
    // cross-fade goes skeleton -> content and the NEW page is never
    // captured dimmed. Removing the class reverts the section fade
    // instantly (see the base transition in common.css).
    pendingState.clear();
  };
  let viewTransition = null;
  // Prefer an element-scoped transition on #MainContent (Chrome 147+,
  // Edge, Opera, WebView): the transition's snapshot pseudo-tree then
  // lives inside <main> and only <main>'s subtree participates — the fixed
  // header/chrome stays out of every capture and animation, so it never
  // fades, translates or flashes during a soft navigation ("content
  // transitions, chrome stays put"). Browsers without Element
  // .startViewTransition (Safari, Firefox, older Chrome) fall back to the
  // document-scoped transition, which the data-soft-nav un-naming keeps
  // free of stale persistent-element captures.
  const mainElement = document.getElementById('MainContent');
  const startVt =
    typeof mainElement?.startViewTransition === 'function'
      ? (cb) => mainElement.startViewTransition(cb)
      : typeof document.startViewTransition === 'function'
        ? (cb) => document.startViewTransition(cb)
        : null;
  if (!traverse && startVt && !prefersReducedMotion()) {
    // Same-document view transitions treat an element with the same
    // view-transition-name in both states as "persistent": the browser
    // reuses the old rendering instead of re-capturing the new state. The
    // header legitimately changes inside the swap (retracted offset,
    // transparent mode), so that reuse paints the stale hidden header
    // through the whole transition — the temporary disappearing header.
    // Un-name the shell for the duration of the soft navigation (must
    // happen BEFORE the old state is captured, i.e. before the call); the
    // header then rides in the live root snapshot and is visible from the
    // new page's first frame. The MPA opt-in in theme.liquid is untouched.
    document.documentElement.setAttribute('data-soft-nav', '');
    viewTransition = startVt(swap);
    try {
      viewTransition.types?.clear?.();
      viewTransition.types?.add?.('page-navigation');
    } catch {
      // types are an optional enhancement
    }
  } else {
    swap();
  }

  if (viewTransition) {
    await viewTransition.finished.catch(() => {});
  }
  // The new page is now the settled, painted state: release the commit
  // transition lock and the soft-nav un-naming (no-op on the
  // reduced-motion/no-transition path, and harmless if a newer navigation
  // already took the document over — the next swap simply sets them
  // again).
  document.documentElement.removeAttribute('data-chrome-committing');
  document.documentElement.removeAttribute('data-soft-nav');

  // A newer navigation (or Escape) may have superseded this one while the
  // transition ran. The DOM swap already happened but this navigation must
  // not claim the finish: aborting here routes through navigate()'s
  // AbortError branch, which stays silent — no end event, no page:view for
  // a page the user already left, no GA4 double-count.
  if (controller.signal.aborted) {
    abort();
  }

  const main = document.getElementById('MainContent');
  await Promise.race([
    executePageScripts(main),
    new Promise((resolve) => setTimeout(resolve, 4000)),
  ]);

  if (traverse) {
    window.scrollTo(0, restoreScroll);
  }

  // Focus contract (see Catsights-软导航军规.md): after the swap and its page
  // scripts have settled, land keyboard/screen-reader position on the new
  // page's #MainContent so a soft navigation never drops the user to <body>
  // (audit P2). Runs for pushed clicks and back/forward traversals alike,
  // and after the view transition finished on BOTH the animated and
  // reduced-motion paths — focus always targets the live, settled DOM.
  // focusMain uses preventScroll, so the scroll established above (restore
  // for a traversal) or in the swap (top / hash anchor for a click) is kept.
  //
  // A superseded navigation (Escape, or a newer navigation that bumped the
  // epoch) never lands focus: the abort guard runs BEFORE the placement, so
  // the only focus placement that runs is the newest navigation's. The mega
  // panel D5 close (no restoreFocus) has already released its trigger before
  // this point, so the final focus order is: panel closed -> focus on main.
  if (controller.signal.aborted) {
    abort();
  }

  focusMain();

  dispatch(display.end, { to: target.href, traverse });
  dispatchPageView(currentTemplate);
}

/**
 * Public API: navigate and lookup helpers, exported for tests and for
 * progressive enhancements.
 */
export const nav = {
  isEligible,
  navigate,
};

/**
 * Navigates to a URL through the runtime. Aborts any in-flight navigation
 * for a different URL (last-wins), and deduplicates identical targets.
 *
 * @param {string} url
 * @param {Object} [options]
 * @param {boolean} [options.traverse]
 * @param {number} [options.restoreScroll]
 */
export async function navigate(url, options = {}) {
  const target = new URL(url, location.href);

  if (inflight) {
    if (inflight.url === target.href) {
      // The original call owns this navigation's abort/error handling (its
      // AbortError settles silently inside its own navigate()). Hand back an
      // isolated copy so a rejection settling through the shared promise
      // (a silent abort) can never surface as an unhandled rejection on
      // this duplicate call chain — e.g. the fire-and-forget click path.
      return inflight.promise.catch(() => {});
    }
    inflight.controller.abort();
    inflight = null;
  }

  const controller = new AbortController();
  const promise = perform(target, { controller, ...options });
  inflight = { url: target.href, controller, promise };

  try {
    await promise;
  } catch (error) {
    if (error && error.name === 'AbortError') {
      // An aborted navigation settles silently, but the progress bar it
      // started must not spin forever. Hide it — unless a newer
      // navigation has already taken the bar over (rapid click: the old
      // navigation must not hide the new one's bar). The pending state
      // class rides the same guard.
      if (!inflight || inflight.url === target.href) {
        progressBar.hide();
        pendingState.clear();
      }
      return;
    }
    dispatch(display.error, { to: target.href, error });
    dispatch(display.fallback, { to: target.href });
    if (options.traverse) {
      location.reload();
    } else {
      // Full-page fallback: reached when the soft path cannot complete
      // (fetch/parse failure or a missing #MainContent). The browser loads
      // the target with its own render-blocking stylesheet pipeline, so the
      // visitor never sees unstyled content.
      location.assign(target.href);
    }
  } finally {
    if (inflight && inflight.url === target.href) {
      inflight = null;
    }
  }
}

/**
 * Click interception. Runs in the capture phase; skips links the browser
 * should handle itself (modifiers, target, downloads, component-managed
 * clicks via on:click, excluded routes).
 *
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (event.defaultPrevented) {
    return;
  }
  if (event.button !== 0) {
    return;
  }
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return;
  }

  const target = event.composedPath ? event.composedPath()[0] : event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const anchor = target.closest('a[href]');
  if (!anchor) {
    return;
  }
  if (!ready) {
    return; // documents parse-then-hydrate: full navigation until ready
  }
  if (anchor.getAttribute('target') && anchor.getAttribute('target') !== '_self') {
    return;
  }
  if (anchor.hasAttribute('download')) {
    return;
  }
  if (anchor.closest('[data-nav-exclude]')) {
    return;
  }
  if (anchor.hasAttribute('on:click')) {
    return; // component-managed link
  }
  const destination = withMarketPrefix(new URL(anchor.href));
  if (!isEligible(destination.href)) {
    return;
  }

  event.preventDefault();
  navigate(destination.href);
}

/**
 * GET-form interception. Forms that navigate (GET, same-origin, eligible,
 * after ready, outside data-nav-exclude) are submitted soft — this covers
 * search and filter forms whose submissions would otherwise be full
 * reloads. POST forms and component-managed forms pass through untouched.
 *
 * Mirrors the browser's own GET-submit URL construction: the action's
 * query is replaced wholesale by the form fields (plus the submitter's
 * name when present).
 *
 * @param {SubmitEvent} event
 */
function onSubmit(event) {
  if (event.defaultPrevented || !ready) {
    return;
  }
  const target = event.target instanceof Element ? event.target : null;
  const form = target?.closest('form');
  if (!form || !form.method) {
    return;
  }
  if (form.method.toLowerCase() !== 'get') {
    return;
  }
  if (form.closest('[data-nav-exclude]')) {
    return;
  }

  const action = form.action ? new URL(form.action, location.href) : new URL(location.href);
  if (action.origin !== location.origin) {
    return;
  }

  const fields = event.submitter
    ? new FormData(form, event.submitter)
    : new FormData(form);
  const params = new URLSearchParams(fields);

  action.search = ''; // GET submits replace the action query, never merge
  for (const [key, value] of params) {
    action.searchParams.set(key, value);
  }

  const destination = withMarketPrefix(action);
  if (!isEligible(destination.href)) {
    return;
  }

  event.preventDefault();
  navigate(destination.href);
}

/**
 * Back/forward through soft navigation entries: swap the cached (or
 * freshly fetched) page and restore its scroll position. Rapid successive
 * traversals are coalesced into the last one so no entry is skipped.
 *
 * @param {PopStateEvent} event
 */
function onPopstate(event) {
  const state = event.state;
  if (!state || !state.navigation) {
    return;
  }
  const target = new URL(location.href);

  if (traversing) {
    pendingTraversal = { url: target.href, scrollY: state.scrollY || 0 };
    return;
  }

  traversing = true;
  navigate(target.href, { traverse: true, restoreScroll: state.scrollY || 0 })
    .catch(() => {})
    .finally(() => {
      traversing = false;
      if (pendingTraversal) {
        const pending = pendingTraversal;
        pendingTraversal = null;
        navigate(pending.url, { traverse: true, restoreScroll: pending.scrollY });
      }
    });
}

/**
 * Escape during an in-flight navigation cancels it without side effects.
 *
 * @param {KeyboardEvent} event
 */
function onKeydown(event) {
  if (event.key !== 'Escape' || !inflight) {
    return;
  }
  inflight.controller.abort();
  inflight = null;
}

const config = readConfig();
rootUrl = config.rootUrl || window.Shopify?.routes?.root || '/'; // locale-ok: last resort; window.Shopify.routes.root is guaranteed on a storefront
currentTemplate = config.templateName;

/**
 * Top progress bar for soft navigations. The element is created lazily on
 * the first navigation (no layout change) and styled entirely from
 * common.css: a 3px fixed top bar with a sliding gradient while a
 * navigation is in flight, faded out once the swap lands or the full-page
 * fallback takes over.
 *
 * The bar is subject to a delayed-display threshold
 * (PROGRESS_BAR_SHOW_DELAY_MS): show() only schedules a reveal, and a
 * navigation that settles before the timer fires cancels it — the bar
 * never materializes. hide() is therefore idempotent for a bar that was
 * never revealed (clearing the pending timer is all there is to do).
 */
const progressBar = {
  element: null,
  hideTimer: null,
  showTimer: null,

  show() {
    // Each start event restarts the pending reveal, so a superseded
    // navigation's timer can never fire into a newer navigation's window
    // (last-wins, matching navigate()'s inflight handling).
    window.clearTimeout(this.showTimer);
    this.showTimer = window.setTimeout(() => {
      this.showTimer = null;
      this.reveal();
    }, PROGRESS_BAR_SHOW_DELAY_MS);
  },

  // The navigation's content has landed (DOM swap ran): the wait the bar
  // measures is over. A bar that was never revealed must never appear —
  // the END event trails the swap by the view transition's animation and
  // page-script settle time, so waiting for it would flash the bar on
  // every fast navigation (the exact flicker P10 removes). A bar that IS
  // visible keeps its existing END-driven hide.
  cancelPendingShow() {
    window.clearTimeout(this.showTimer);
    this.showTimer = null;
  },

  reveal() {
    if (!this.element || !this.element.isConnected) {
      this.element = document.createElement('div');
      this.element.className = 'nav-progress';
      this.element.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(this.element);
    }
    window.clearTimeout(this.hideTimer);
    // Restart the slide even when a previous navigation just finished.
    this.element.classList.remove('nav-progress--active');
    void this.element.offsetWidth;
    this.element.classList.add('nav-progress--active');
    this.element.classList.remove('nav-progress--done');
  },

  hide() {
    // Cancel a pending reveal first: for a navigation that settled inside
    // the display threshold the bar was never shown, and this early return
    // is the whole job — no element, no classes, no leftover timers.
    window.clearTimeout(this.showTimer);
    this.showTimer = null;
    if (!this.element || !this.element.isConnected) {
      return;
    }
    this.element.classList.remove('nav-progress--active');
    this.element.classList.add('nav-progress--done');
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.element?.remove();
      this.element = null;
    }, 350);
  },
};

document.addEventListener(display.start, (event) => {
  progressBar.show();
  // History traversals swap synchronously (usually from the page cache) —
  // there is no wait to cover, so the pending look never applies to them.
  if (!event.detail?.traverse) {
    pendingState.apply();
  }
});
for (const finished of [display.end, display.error, display.fallback]) {
  document.addEventListener(finished, () => {
    progressBar.hide();
    pendingState.clear();
  });
}

/**
 * Body state class for the pending (slow-network skeleton) look. Every
 * access is defensive because the runtime is imported under DOM mocks by
 * the test suites (tmp/nav-test): the mocks stub the events and the swap
 * callback but not necessarily classList or attributes — a hard property
 * access here would throw inside their swap simulation.
 */
const pendingState = {
  apply() {
    const body = document.body;
    if (body && typeof body.classList?.add === 'function') {
      body.classList.add(NAV_PENDING_CLASS);
    }
    const main = document.getElementById('MainContent');
    if (main && typeof main.setAttribute === 'function') {
      main.setAttribute('aria-busy', 'true');
    }
  },

  clear() {
    const body = document.body;
    if (body && typeof body.classList?.remove === 'function') {
      body.classList.remove(NAV_PENDING_CLASS);
    }
    const main = document.getElementById('MainContent');
    if (main && typeof main.removeAttribute === 'function') {
      main.removeAttribute('aria-busy');
    }
  },
};

whenReady(() => {
  // By now every script the initial document parsed has executed, so the
  // seeded set only ever contains scripts that truly ran.
  seedExecutedScripts();
  ready = true;
  dispatchPageView(currentTemplate);
});

if (!window.Shopify || !window.Shopify.designMode) {
  document.addEventListener('click', onClick, true);
  /* Bubble phase, NOT capture. Component-managed forms (the header search and
     the search page) call preventDefault() from their own submit handlers on
     the form element, which runs at target phase. A capture listener here
     would run first, see defaultPrevented === false, and soft-navigate anyway:
     an empty query would load /search?q= and an IME-confirmation Enter while
     composing Chinese would navigate away mid-input. Bubbling lets the form
     decide first and keeps isEligible() as the only gate for the rest. */
  document.addEventListener('submit', onSubmit, false);
  window.addEventListener('popstate', onPopstate);
  document.addEventListener('keydown', onKeydown);
}